/**
 * Steering Tab Component
 *
 * Admin interface for monitoring STEERING BRAIN activity.
 * Shows sessions, messages, function calls for deep debugging.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

interface SteeringSession {
  id: string;
  user_id: number;
  status: string;
  trigger_type: string;
  trigger_reason: string;
  started_at: string;
  completed_at: string | null;
  total_tokens: number;
  reasoning_tokens: number;
  processing_time_ms: number | null;
  error_message: string | null;
  message_count: number;
  function_call_count: number;
}

interface SteeringMessage {
  id: number;
  message_order: number;
  role: string;
  content: string;
  timestamp: string;
}

interface SteeringFunctionCall {
  id: string;
  call_order: number;
  function_name: string;
  arguments: string;
  result: string | null;
  success: number;
  timestamp: string;
}

interface SessionDetail extends SteeringSession {
  messages: SteeringMessage[];
  function_calls: SteeringFunctionCall[];
}

interface UserWithActivity {
  user_id: number;
  session_count: number;
  last_activity: string;
  total_tokens: number;
}

export default function SteeringTab() {
  const { t } = useTranslation('admin');
  const { token } = useAuth();
  const [users, setUsers] = useState<UserWithActivity[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [sessions, setSessions] = useState<SteeringSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<SessionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load users with STEERING activity
  useEffect(() => {
    loadUsers();
  }, []);

  // Load sessions when user is selected
  useEffect(() => {
    if (selectedUserId) {
      loadSessions(selectedUserId);
    } else {
      loadAllSessions();
    }
  }, [selectedUserId]);

  const loadUsers = async () => {
    try {
      const response = await api.get('/steering/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const loadSessions = async (userId: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/steering/sessions?user_id=${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data.sessions || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('steering.failedToLoadSessions'));
    } finally {
      setLoading(false);
    }
  };

  const loadAllSessions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/steering/sessions', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data.sessions || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('steering.failedToLoadSessions'));
    } finally {
      setLoading(false);
    }
  };

  const loadSessionDetail = async (sessionId: string) => {
    try {
      const response = await api.get(`/steering/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedSession(response.data);
    } catch (err: any) {
      console.error('Failed to load session detail:', err);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const formatDuration = (ms: number | null) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}min`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'error': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'system': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'user': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'assistant': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with user filter */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t('steering.title')}</h2>
          <p className="text-sm text-gray-500">
            {t('steering.description')}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={selectedUserId || ''}
            onChange={(e) => setSelectedUserId(e.target.value ? Number(e.target.value) : null)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="">{t('steering.allUsers')}</option>
            {users.map(u => (
              <option key={u.user_id} value={u.user_id}>
                {t('steering.userLabel', { id: u.user_id, count: u.session_count })}
              </option>
            ))}
          </select>
          <button
            onClick={() => selectedUserId ? loadSessions(selectedUserId) : loadAllSessions()}
            className="px-3 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors"
          >
            {t('steering.refresh')}
          </button>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sessions List */}
        <div className="lg:col-span-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">{t('steering.sessionsLabel')}</h3>
          </div>
          <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-gray-500">{t('common.loading')}</div>
            ) : sessions.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                {t('steering.noSteeringSessions')}
              </div>
            ) : (
              sessions.map(session => (
                <button
                  key={session.id}
                  onClick={() => loadSessionDetail(session.id)}
                  className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                    selectedSession?.id === session.id ? 'bg-purple-50' : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-mono text-gray-500">{session.id}</span>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(session.status)}`}>
                      {session.status}
                    </span>
                  </div>
                  <div className="text-sm text-gray-700 truncate">
                    {session.trigger_reason || session.trigger_type}
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    <span>{t('steering.user')} {session.user_id}</span>
                    <span>{session.message_count} {t('steering.msgs')}</span>
                    <span>{session.function_call_count} {t('steering.calls')}</span>
                    <span>{formatDuration(session.processing_time_ms)}</span>
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {formatDate(session.started_at)}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Session Detail - Full Chat View */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">
              {selectedSession ? t('steering.sessionLabel', { id: selectedSession.id }) : t('steering.selectSession')}
            </h3>
            {selectedSession && (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span>{selectedSession.total_tokens} tokens</span>
                <span className="text-purple-600">({selectedSession.reasoning_tokens} reasoning)</span>
              </div>
            )}
          </div>

          {selectedSession ? (
            <div className="max-h-[600px] overflow-y-auto">
              {/* Messages Section */}
              <div className="p-4 space-y-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t('steering.conversation')} ({selectedSession.messages.length} {t('steering.messages')})
                </h4>
                {selectedSession.messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`p-3 rounded-lg border ${getRoleColor(msg.role)}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold uppercase">{msg.role}</span>
                      <span className="text-xs opacity-60">{formatDate(msg.timestamp)}</span>
                    </div>
                    <pre className="text-sm whitespace-pre-wrap font-mono overflow-x-auto max-h-96">
                      {msg.content}
                    </pre>
                  </div>
                ))}
              </div>

              {/* Function Calls Section */}
              {selectedSession.function_calls.length > 0 && (
                <div className="p-4 border-t border-gray-200 space-y-3">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t('steering.functionCalls')} ({selectedSession.function_calls.length})
                  </h4>
                  {selectedSession.function_calls.map((call) => (
                    <div
                      key={call.id}
                      className={`p-3 rounded-lg border ${
                        call.success ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-900">
                          {call.function_name}()
                        </span>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          call.success ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {call.success ? t('steering.success') : t('gdpr.failed')}
                        </span>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <span className="text-xs font-medium text-gray-500">{t('steering.arguments')}</span>
                          <pre className="text-xs bg-white/50 p-2 rounded mt-1 overflow-x-auto">
                            {JSON.stringify(JSON.parse(call.arguments), null, 2)}
                          </pre>
                        </div>
                        {call.result && (
                          <div>
                            <span className="text-xs font-medium text-gray-500">{t('steering.result')}</span>
                            <pre className="text-xs bg-white/50 p-2 rounded mt-1 overflow-x-auto">
                              {JSON.stringify(JSON.parse(call.result), null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Error Message */}
              {selectedSession.error_message && (
                <div className="p-4 border-t border-gray-200">
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <span className="text-xs font-semibold text-red-700 uppercase">{t('steering.errorLabel')}</span>
                    <pre className="text-sm text-red-600 mt-1 whitespace-pre-wrap">
                      {selectedSession.error_message}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-16 text-center text-gray-400">
              <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <p className="text-sm">{t('steering.selectSessionDescription')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
